/* collection-infinite-scroll.js — External infinite scroll controller
 *
 * Lives outside #ProductGridContainer so it survives facet AJAX replacements.
 * Listens for 'infinite-scroll:refresh' event dispatched by facets.js after
 * filter/sort updates, then destroys and reinitializes with fresh DOM refs.
 *
 * Next-page URL is derived from rendered pagination links in the DOM,
 * NOT <link rel="next"> in <head> (stale after AJAX).
 */
(function () {
  'use strict';

  let observer = null;
  let isFetching = false;
  var retryCount = 0;
  var maxRetries = 3;
  var retryDelayMs = 1500;

  /**
   * Get next page URL from rendered pagination inside the current DOM.
   * The pagination is hidden via CSS but the markup is present and correct
   * after both initial render and facet AJAX replacement.
   */
  function getNextPageUrl() {
    // Pagination next link (most reliable after facet AJAX)
    // NOTE: Dawn's pagination.liquid uses --prev class on the "next page" link
    // and --next class on the "previous page" link (classes are swapped).
    const paginationNext =
      document.querySelector('.pagination .pagination__item--prev') ||
      document.querySelector('nav.pagination a[rel="next"]') ||
      document.querySelector('nav.pagination a[aria-label*="Next"]');
    if (paginationNext?.href) return paginationNext.href;

    // Fallback: <link rel="next"> in <head> (only reliable on initial load)
    const headLink = document.querySelector('link[rel="next"]');
    if (headLink?.href) return headLink.href;

    return null;
  }

  /**
   * Destroy any existing IntersectionObserver and reset state.
   */
  function destroy() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    isFetching = false;
  }

  /**
   * Initialize infinite scroll from live DOM.
   * Safe to call multiple times — destroys previous observer first.
   */
  function init() {
    destroy();

    const sentinel = document.getElementById('InfiniteSentinel');
    const grid = document.getElementById('product-grid');
    const spinner = document.getElementById('InfiniteSpinner');

    if (!sentinel || !grid) return;

    let nextURL = getNextPageUrl();

    // No more pages — hide sentinel
    if (!nextURL) {
      sentinel.style.display = 'none';
      return;
    }

    // Ensure sentinel is visible (may have been hidden by a previous session)
    sentinel.style.display = 'flex';

    observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (!entry || !entry.isIntersecting || isFetching) return;

        if (!nextURL) {
          sentinel.style.display = 'none';
          destroy();
          return;
        }

        isFetching = true;
        observer.unobserve(sentinel);
        if (spinner) spinner.style.opacity = '1';

        fetch(nextURL, { credentials: 'same-origin' })
          .then(function (resp) {
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            return resp.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');

            // Append new product cards
            var fragment = document.createDocumentFragment();
            doc.querySelectorAll('#product-grid > li').forEach(function (li) {
              li.classList.add('fade-in');
              fragment.appendChild(li);
            });
            grid.appendChild(fragment);

            // Initialize product cards (idempotent — guarded by dataset.initialized)
            if (window.initOCSCard) {
              grid.querySelectorAll('.ocs-card').forEach(function (card) {
                window.initOCSCard(card);
              });
            }

            // Get next page from the FETCHED page's pagination (not current DOM head)
            // NOTE: --prev class = actual "next page" link (Dawn class names are swapped)
            var nextPageLink =
              doc.querySelector('.pagination .pagination__item--prev') ||
              doc.querySelector('nav.pagination a[rel="next"]') ||
              doc.querySelector('nav.pagination a[aria-label*="Next"]') ||
              doc.querySelector('link[rel="next"]');

            nextURL = nextPageLink ? nextPageLink.href : null;
            retryCount = 0;

            if (nextURL && observer) {
              observer.observe(sentinel);
            } else {
              sentinel.style.display = 'none';
              destroy();
            }
          })
          .catch(function (err) {
            console.error('Infinite scroll load failed:', err);

            retryCount += 1;

            if (retryCount <= maxRetries && observer && sentinel) {
              setTimeout(function () {
                if (observer && sentinel) observer.observe(sentinel);
              }, retryDelayMs);
            } else if (sentinel) {
              sentinel.style.display = 'none';
              destroy();
            }
          })
          .finally(function () {
            isFetching = false;
            if (spinner) spinner.style.opacity = '.2';
          });
      },
      { rootMargin: '1200px 0px 1200px 0px' }
    );

    observer.observe(sentinel);
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize after facet AJAX replacement (facets.js dispatches this)
  document.addEventListener('infinite-scroll:refresh', function () {
    // Small delay to let facets.js finish updating pagination markup
    setTimeout(init, 150);
  });

  // Reinitialize after Shopify theme editor events
  document.addEventListener('shopify:section:load', init);
})();
